import type { Incident, Supervisor, Agency, Route } from '../composables/useMapData'
import { useAuthStore } from '@/stores/auth'
import { getApiOriginUrl } from '@/config/runtimeConfig'
import { fetchAllPointPages, type PointPageEnvelope } from '@/services/pointCatalogue'

// P12-TEST1A-FE2: the API origin is resolved lazily at request time (see makeRequest /
// getApiBaseUrl). Resolving it here at module evaluation crashed the release bundle with a
// TDZ on runtimeConfig's module-level `env`; getApiOriginUrl() stays fail-closed at use.

// Toggle de mocks (opt-in). Si no está definido, por defecto se usan endpoints reales
const USE_MAP_MOCKS = (import.meta as any)?.env?.VITE_ENABLE_MAP_MOCKS === 'true'

const API_ENDPOINTS = {
  incidents: '/api/incidences',
  supervisors: '/api/users',
  agencies: '/api/points',  // ✅ CORREGIDO: Usar tabla points (agencias reales) en lugar de departments
  routes: '/api/routes',
  analytics: '/api/analytics',
  reports: '/api/reports'
}

// Utilidades de request
class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public endpoint: string
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const makeRequest = async <T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> => {
  const authStore = useAuthStore()
  const url = `${getApiOriginUrl()}${endpoint}`
  
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(authStore.token && { 'Authorization': `Bearer ${authStore.token}` }),
        ...options.headers,
      },
    })

    if (!response.ok) {
      throw new ApiError(
        `HTTP ${response.status}: ${response.statusText}`,
        response.status,
        endpoint
      )
    }

    const data = await response.json()
    return data
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }
    
    // Manejo de errores de red o parsing
    console.error(`Error in request to ${endpoint}:`, error)
    throw new ApiError(
      `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`,
      0,
      endpoint
    )
  }
}

// Datos mock para desarrollo (se eliminarán cuando el backend esté listo)
const generateMockIncidents = (): Incident[] => {
  const priorities: Incident['priority'][] = ['BAJA', 'MEDIA', 'ALTA', 'CRITICA']
  const statuses: Incident['status'][] = ['ABIERTA', 'EN_PROGRESO', 'RESUELTA', 'CERRADA']
  const categories = [
    'Accidente de Tránsito', 'Robo/Hurto', 'Disturbios', 'Emergencia Médica',
    'Incendio', 'Vandalismo', 'Drogas', 'Violencia Doméstica'
  ]
  
  // Coordenadas de República Dominicana (con dispersión realista)
  const baseLat = 18.7357
  const baseLng = -70.1627
  
  return Array.from({ length: 45 }, (_, i) => ({
    id: `INC-${String(i + 1).padStart(4, '0')}`,
    title: `Incidencia ${i + 1}`,
    description: `Descripción detallada de la incidencia ${i + 1}`,
    priority: priorities[Math.floor(Math.random() * priorities.length)],
    status: statuses[Math.floor(Math.random() * statuses.length)],
    latitude: baseLat + (Math.random() - 0.5) * 2,
    longitude: baseLng + (Math.random() - 0.5) * 3,
    createdAt: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000).toISOString(),
    category: categories[Math.floor(Math.random() * categories.length)],
    assignedSupervisor: Math.random() > 0.3 ? `SUP-${String(Math.floor(Math.random() * 15) + 1).padStart(3, '0')}` : undefined,
    affectedAgencies: Math.random() > 0.7 ? [`AG-${String(Math.floor(Math.random() * 20) + 1).padStart(3, '0')}`] : undefined
  }))
}

const generateMockSupervisors = (): Supervisor[] => {
  const statuses: Supervisor['status'][] = ['ACTIVO', 'INACTIVO', 'EN_RUTA', 'DISPONIBLE']
  const zones = ['Norte', 'Sur', 'Este', 'Oeste', 'Centro', 'Nordeste', 'Noroeste', 'Suroeste']
  const names = [
    'Carlos Martínez', 'Ana Rodríguez', 'Luis García', 'María Fernández',
    'Pedro Jiménez', 'Carmen López', 'José Hernández', 'Isabel González',
    'Miguel Díaz', 'Rosa Pérez', 'Antonio Ruiz', 'Elena Morales',
    'Francisco Santos', 'Lucía Romero', 'Rafael Torres'
  ]
  
  const baseLat = 18.7357
  const baseLng = -70.1627
  
  return Array.from({ length: 15 }, (_, i) => ({
    id: `SUP-${String(i + 1).padStart(3, '0')}`,
    name: names[i],
    email: `supervisor${i + 1}@mgi.gob.do`,
    phone: `+1-809-${String(Math.floor(Math.random() * 9000000) + 1000000)}`,
    status: statuses[Math.floor(Math.random() * statuses.length)],
    currentLatitude: Math.random() > 0.2 ? baseLat + (Math.random() - 0.5) * 2 : undefined,
    currentLongitude: Math.random() > 0.2 ? baseLng + (Math.random() - 0.5) * 3 : undefined,
    assignedZone: zones[Math.floor(Math.random() * zones.length)],
    activeIncidents: Array.from({ length: Math.floor(Math.random() * 4) }, 
      () => `INC-${String(Math.floor(Math.random() * 45) + 1).padStart(4, '0')}`
    ),
    lastUpdate: new Date(Date.now() - Math.random() * 2 * 60 * 60 * 1000).toISOString()
  }))
}

const generateMockAgencies = (): Agency[] => {
  const statuses: Agency['status'][] = ['ACTIVA', 'INACTIVA', 'MANTENIMIENTO']
  const zones = ['Norte', 'Sur', 'Este', 'Oeste', 'Centro', 'Nordeste', 'Noroeste', 'Suroeste']
  const agencyTypes = ['Policía Nacional', 'AMET', 'Bomberos', 'Defensa Civil', 'DNCD', 'Migración']
  
  const baseLat = 18.7357
  const baseLng = -70.1627
  
  return Array.from({ length: 28 }, (_, i) => ({
    id: `AG-${String(i + 1).padStart(3, '0')}`,
    name: `${agencyTypes[Math.floor(Math.random() * agencyTypes.length)]} - Unidad ${i + 1}`,
    code: `U${String(i + 1).padStart(3, '0')}`,
    address: `Dirección de la Unidad ${i + 1}, República Dominicana`,
    latitude: baseLat + (Math.random() - 0.5) * 2,
    longitude: baseLng + (Math.random() - 0.5) * 3,
    status: statuses[Math.floor(Math.random() * statuses.length)],
    zone: zones[Math.floor(Math.random() * zones.length)],
    contactInfo: {
      phone: `+1-809-${String(Math.floor(Math.random() * 9000000) + 1000000)}`,
      email: `unidad${i + 1}@mgi.gob.do`,
      manager: `Encargado Unidad ${i + 1}`
    },
    incidentCount: Math.floor(Math.random() * 20),
    lastIncident: Math.random() > 0.3 ? new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString() : undefined
  }))
}

const generateMockRoutes = (): Route[] => {
  const statuses: Route['status'][] = ['ACTIVA', 'PAUSADA', 'COMPLETADA']
  const routeNames = [
    'Patrullaje Centro Histórico', 'Ronda Zona Comercial', 'Supervisión Carreteras',
    'Patrullaje Fronterizo', 'Ronda Barrios', 'Supervisión Puertos',
    'Patrullaje Turístico', 'Ronda Industrial', 'Supervisión Aeropuertos'
  ]
  
  const baseLat = 18.7357
  const baseLng = -70.1627
  
  return Array.from({ length: 12 }, (_, i) => {
    const routeStart = {
      lat: baseLat + (Math.random() - 0.5) * 2,
      lng: baseLng + (Math.random() - 0.5) * 3
    }
    
    const checkpointCount = Math.floor(Math.random() * 5) + 3
    const coordinates: Array<[number, number]> = []
    const checkpoints = []
    
    for (let j = 0; j < checkpointCount; j++) {
      const lat = routeStart.lat + (Math.random() - 0.5) * 0.1
      const lng = routeStart.lng + (Math.random() - 0.5) * 0.1
      
      coordinates.push([lat, lng])
      checkpoints.push({
        id: `CP-${i + 1}-${j + 1}`,
        name: `Checkpoint ${j + 1}`,
        latitude: lat,
        longitude: lng,
        status: Math.random() > 0.3 ? 'VISITADO' : 'PENDIENTE' as 'PENDIENTE' | 'VISITADO' | 'OMITIDO',
        visitedAt: Math.random() > 0.5 ? new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000).toISOString() : undefined
      })
    }
    
    return {
      id: `RT-${String(i + 1).padStart(3, '0')}`,
      name: routeNames[Math.floor(Math.random() * routeNames.length)],
      description: `Descripción detallada de la ruta ${i + 1}`,
      status: statuses[Math.floor(Math.random() * statuses.length)],
      supervisorId: `SUP-${String(Math.floor(Math.random() * 15) + 1).padStart(3, '0')}`,
      coordinates,
      checkpoints,
      estimatedDuration: Math.floor(Math.random() * 180) + 60, // 60-240 minutos
      actualDuration: Math.random() > 0.5 ? Math.floor(Math.random() * 200) + 50 : undefined,
      createdAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString()
    }
  })
}

// Servicio principal del mapa
export const mapService = {
  // Métodos de datos principales
  async getIncidents(): Promise<Incident[]> {
    try {
      // 🔐 Verificar rol del usuario para filtrar incidencias por departamento
      const authStore = useAuthStore()
      const userRole = authStore.userRole
      let endpoint = API_ENDPOINTS.incidents
      
      // Si es DepartmentHead, usar endpoint específico que filtra por departamento
      if (userRole === 'department_head') {
        console.log('🏢 DepartmentHead detectado: filtrando incidencias por departamento')
        endpoint = '/api/department-head/incidences'
      }
      
      // Intentar API real primero
      const response = await makeRequest<any>(endpoint)
      
      // El backend puede devolver datos en formato { data: [...] } o directamente [...]
      let incidents = response
      if (response && Array.isArray(response.data)) {
        incidents = response.data
      } else if (Array.isArray(response)) {
        incidents = response
      } else {
        console.warn('Formato de respuesta inesperado:', response)
        return USE_MAP_MOCKS ? generateMockIncidents() : []
      }
      
      // Convertir datos del backend al formato esperado por el mapa.
      // P13-V1D (C10): las coordenadas NUNCA se fabrican en el camino REAL — una incidencia sin
      // lat/lng persistida se OMITE honestamente de la capa (antes se le inyectaba una posición
      // aleatoria alrededor de Santo Domingo, pintando datos falsos como reales).
      const mappedIncidents = incidents
        .map((incident: any) => {
          const lat = incident.latitude ?? incident.location?.lat ?? null
          const lng = incident.longitude ?? incident.location?.lng ?? null
          if (lat == null || lng == null || !incident.id) return null
          return {
            id: incident.id.toString(),
            title: incident.title || `Incidencia #${incident.id}`,
            description: incident.description || 'Sin descripción',
            priority: mapPriorityToMapFormat(incident.priority) || 'MEDIA',
            status: mapStatusToMapFormat(incident.status) || 'ABIERTA',
            category: incident.category || 'General',
            latitude: Number(lat),
            longitude: Number(lng),
            createdAt: incident.createdAt || new Date().toISOString(),
            updatedAt: incident.updatedAt || new Date().toISOString(),
            assignedSupervisor: incident.assignedTo ?
              (incident.assignedTo.name || `Supervisor #${incident.assignedTo.id}`) :
              undefined,
            affectedAgencies: incident.affectedAgencies || []
          }
        })
        .filter((x: any) => x !== null)

      return mappedIncidents
    } catch (error) {
      console.error('Error fetching incidents:', error)
      // Fallback a datos mock solo si está habilitado explícitamente
      return USE_MAP_MOCKS ? generateMockIncidents() : []
    }
  },

  async getSupervisors(): Promise<Supervisor[]> {
    try {
      // Intentar API real primero
      const response = await makeRequest<any>(API_ENDPOINTS.supervisors)
      
      // El backend puede devolver datos en formato { data: [...] } o directamente [...]
      let users = response
      if (response && Array.isArray(response.data)) {
        users = response.data
      } else if (response && Array.isArray(response.users)) {
        // Soportar forma paginada: { users: [...], total, page, ... }
        users = response.users
      } else if (Array.isArray(response)) {
        users = response
      } else {
        console.warn('Formato de respuesta inesperado para supervisores:', response)
        return USE_MAP_MOCKS ? generateMockSupervisors() : []
      }
      
      // Filtrar solo supervisores y convertir al formato esperado
      const supervisors = users
        .filter((user: any) => 
          user.role && (
            user.role.includes('supervisor') || 
            user.role.includes('field') ||
            user.role.includes('SUPERVISOR') ||
            user.role.includes('FIELD')
          )
        )
        // P13-V5A-R5 · este .map() corre sobre la respuesta VIVA de GET /api/users (camino real,
        // no generador de mocks). Se retiran tres fabricaciones que se emitían como si fueran
        // datos medidos:
        //
        // 1) POSICIÓN. Un supervisor sin posición almacenada recibía, como último término de un
        //    encadenamiento falsy, una coordenada generada al azar alrededor de Santo Domingo,
        //    bajo un comentario que decía usar "coordenadas simuladas si no existen". El productor
        //    no publica posición: el modelo User del backend no tiene currentLatitude,
        //    currentLongitude ni location, y el listado de usuarios serializa la fila tal cual,
        //    así que el 100% de los pines eran inventados y personas reales (id y email reales)
        //    quedaban clavadas en un punto falso del mapa que /super-admin muestra en su fila
        //    principal. La posición real vive en otra superficie (SupervisorPosition / GPS) que
        //    este servicio no lee; conectarla es una decisión del vertical de tracking, no de esta
        //    retirada. Un supervisor sin posición se emite SIN coordenadas y la capa ya lo omite
        //    honestamente (useMapLayers.updateSupervisorLayer filtra los que no las tienen). No se
        //    inventa nada para rellenar el hueco.
        //
        // 2) FRESCURA. lastUpdate caía en la hora actual del navegador cuando faltaba
        //    user.last_login — campo que NO existe en el payload de /api/users (el modelo tiene
        //    last_activity, nunca last_login), de modo que TODOS los supervisores quedaban
        //    sellados con el instante de la carga y el popup los pintaba como recientes. Sin marca
        //    de tiempo medida no hay frescura: se emite vacío y el popup declara la ausencia.
        //
        // 3) IDENTIDAD. id caía en un identificador aleatorio con el que los botones del popup
        //    ("Ver Perfil", "Asignar Tarea") despachaban eventos contra un supervisor inexistente.
        //    Una fila sin id se omite, igual que ya hace el mapper de incidencias de arriba
        //    (P13-V1D C10).
        .map((user: any) => {
          if (user.id == null) return null

          // `??` y no `||`: la coordenada 0 es una coordenada real y debe sobrevivir.
          const currentLatitude = toFiniteCoordinate(user.currentLatitude ?? user.location?.lat)
          const currentLongitude = toFiniteCoordinate(user.currentLongitude ?? user.location?.lng)
          const hasPosition = currentLatitude !== undefined && currentLongitude !== undefined

          return {
            id: user.id.toString(),
            name: user.fullName || user.username || `Usuario ${user.id}`,
            email: user.email || 'Sin email',
            phone: user.phone || 'Sin teléfono',
            status: user.is_active ? 'ACTIVO' : 'INACTIVO',
            currentLatitude: hasPosition ? currentLatitude : undefined,
            currentLongitude: hasPosition ? currentLongitude : undefined,
            assignedZone: user.department || user.zone || 'Sin zona',
            activeIncidents: [],
            lastUpdate: user.last_login ?? ''
          }
        })
        .filter((x: any) => x !== null)
      
      return supervisors.length > 0 ? supervisors : (USE_MAP_MOCKS ? generateMockSupervisors() : [])
    } catch (error) {
      console.error('Error fetching supervisors:', error)
      return USE_MAP_MOCKS ? generateMockSupervisors() : []
    }
  },

  async getAgencies(): Promise<Agency[]> {
    try {
      // P13-PROD-SCALE-BOUNDED-READS-WS1: /api/points answers one bounded page. The map draws EVERY agency
      // and counts them, so every page is read, or the read fails into the catch below (never a subset).
      const points = await fetchAllPointPages((query) => makeRequest<PointPageEnvelope>(
        `${API_ENDPOINTS.agencies}?${new URLSearchParams(Object.entries(query).map(([k, v]) => [k, String(v)])).toString()}`
      ))
      
      // Convertir points (agencias reales) a formato de agencias para el mapa
      // ✅ FILTRAR: Solo puntos con coordenadas GPS reales
      const agencies = points
        .filter((point: any) => 
          point.latitude != null && 
          point.longitude != null &&
          !isNaN(parseFloat(point.latitude)) &&
          !isNaN(parseFloat(point.longitude))
        )
        .map((point: any) => ({
          id: point.id?.toString() || Math.random().toString(),
          name: point.name || `Agencia ${point.id}`,
          code: point.code || `AG${point.id}`,
          zone: point.zone || point.region || 'Sin zona',
          status: point.is_active ? 'ACTIVA' : 'INACTIVA',
          // ✅ USAR COORDENADAS REALES - No generar aleatorias
          latitude: parseFloat(point.latitude),
          longitude: parseFloat(point.longitude),
          address: point.address || 'Dirección no disponible',
          incidentCount: point.incident_count || 0,
          lastIncident: point.last_incident || undefined,
          contactInfo: {
            phone: point.phone || point.contact_phone || 'Sin teléfono',
            email: point.email || point.contact_email || 'Sin email',
            manager: point.manager || point.contact_name || 'Sin gerente'
          }
        }))
      
      console.log(`✅ Agencias cargadas desde /api/points: ${agencies.length}`)
      return agencies.length > 0 ? agencies : (USE_MAP_MOCKS ? generateMockAgencies() : [])
    } catch (error) {
      console.error('Error fetching agencies:', error)
      return USE_MAP_MOCKS ? generateMockAgencies() : []
    }
  },

  async getRoutes(): Promise<Route[]> {
    try {
      if (USE_MAP_MOCKS) {
        await new Promise(resolve => setTimeout(resolve, 350))
        return generateMockRoutes()
      }
      const response = await makeRequest<any>(API_ENDPOINTS.routes)
      let routes: any[] = []

      if (Array.isArray(response)) {
        routes = response
      } else if (response && Array.isArray(response.data)) {
        routes = response.data
      } else if (response && Array.isArray(response.routes)) {
        routes = response.routes
      } else {
        console.warn('Formato de respuesta inesperado para rutas:', response)
        return USE_MAP_MOCKS ? generateMockRoutes() : []
      }

      // Normalizar forma de rutas al contrato del mapa
      const mapped: Route[] = routes.map((r: any) => ({
        id: r.id?.toString() || cryptoRandomId(),
        name: r.name || `Ruta ${r.id || ''}`,
        description: r.description || 'Sin descripción',
        status: mapRouteStatus(r.status),
        supervisorId: r.supervisorId?.toString?.() || r.supervisor_id?.toString?.() || 'N/A',
        coordinates: Array.isArray(r.coordinates)
          ? r.coordinates
          : Array.isArray(r.points)
            ? r.points.map((p: any) => [p.latitude || p.lat, p.longitude || p.lng])
            : [],
        checkpoints: Array.isArray(r.checkpoints)
          ? r.checkpoints.map((cp: any) => ({
              id: cp.id?.toString() || cryptoRandomId(),
              name: cp.name || 'Checkpoint',
              latitude: cp.latitude || cp.lat,
              longitude: cp.longitude || cp.lng,
              status: (cp.status as any) || 'PENDIENTE',
              visitedAt: cp.visitedAt || cp.visited_at
            }))
          : [],
        estimatedDuration: Number(r.estimatedDuration || r.estimated_duration || 0),
        actualDuration: r.actualDuration || r.actual_duration,
        createdAt: r.createdAt || r.created_at || new Date().toISOString()
      }))
      return mapped
    } catch (error) {
      console.error('Error fetching routes:', error)
      return USE_MAP_MOCKS ? generateMockRoutes() : []
    }
  },

  // Métodos específicos para elementos individuales
  async getIncidentById(id: string): Promise<Incident | null> {
    try {
      if (USE_MAP_MOCKS) {
        const incidents = generateMockIncidents()
        return incidents.find(inc => inc.id === id) || null
      }

      return await makeRequest<Incident>(`${API_ENDPOINTS.incidents}/${id}`)
    } catch (error) {
      console.error(`Error fetching incident ${id}:`, error)
      return null
    }
  },

  async getSupervisorById(id: string): Promise<Supervisor | null> {
    try {
      if (USE_MAP_MOCKS) {
        const supervisors = generateMockSupervisors()
        return supervisors.find(sup => sup.id === id) || null
      }

      return await makeRequest<Supervisor>(`${API_ENDPOINTS.supervisors}/${id}`)
    } catch (error) {
      console.error(`Error fetching supervisor ${id}:`, error)
      return null
    }
  },

  // Métodos de actualización
  async updateIncidentStatus(id: string, status: Incident['status']): Promise<boolean> {
    try {
      if (USE_MAP_MOCKS) {
        await new Promise(resolve => setTimeout(resolve, 200))
        return true // Simular éxito
      }

      await makeRequest(`${API_ENDPOINTS.incidents}/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
      })
      return true
    } catch (error) {
      console.error(`Error updating incident ${id} status:`, error)
      return false
    }
  },

  async updateSupervisorLocation(
    id: string, 
    latitude: number, 
    longitude: number
  ): Promise<boolean> {
    try {
      if (USE_MAP_MOCKS) {
        await new Promise(resolve => setTimeout(resolve, 200))
        return true
      }

      await makeRequest(`${API_ENDPOINTS.supervisors}/${id}/location`, {
        method: 'PATCH',
        body: JSON.stringify({ latitude, longitude })
      })
      return true
    } catch (error) {
      console.error(`Error updating supervisor ${id} location:`, error)
      return false
    }
  },

  // Métodos de análisis y reportes
  async getMapAnalytics(params: {
    dateFrom?: string
    dateTo?: string
    zone?: string
  } = {}): Promise<{
    totalIncidents: number
    incidentsByPriority: Record<string, number>
    incidentsByCategory: Record<string, number>
    responseTimeAvg: number
    resolutionRate: number
  }> {
    try {
      if (USE_MAP_MOCKS) {
        await new Promise(resolve => setTimeout(resolve, 600))

        return {
          totalIncidents: 45,
          incidentsByPriority: {
            'CRITICA': 8,
            'ALTA': 12,
            'MEDIA': 15,
            'BAJA': 10
          },
          incidentsByCategory: {
            'Accidente de Tránsito': 12,
            'Robo/Hurto': 8,
            'Disturbios': 6,
            'Emergencia Médica': 10,
            'Incendio': 4,
            'Vandalismo': 3,
            'Drogas': 2
          },
          responseTimeAvg: 24.5,
          resolutionRate: 87.3
        }
      }

      const queryParams = new URLSearchParams(params as Record<string, string>)
      return await makeRequest(`${API_ENDPOINTS.analytics}/map?${queryParams}`)
    } catch (error) {
      console.error('Error fetching map analytics:', error)
      throw error
    }
  },

  // Método para verificar conectividad con el backend
  async checkConnection(): Promise<boolean> {
    try {
      await makeRequest('/api/health')
      return true
    } catch (error) {
      if (USE_MAP_MOCKS) {
        console.warn('Backend connection failed, using mock data')
      } else {
        console.warn('Backend connection failed')
      }
      return false
    }
  },

  // Configuración y utilidades
  getApiEndpoints() {
    return { ...API_ENDPOINTS }
  },

  getApiBaseUrl() {
    return getApiOriginUrl()
  },

  setApiBaseUrl(url: string) {
    // Esta función permitirá cambiar la URL base en tiempo de ejecución
    // Útil para testing o cambios dinámicos de entorno
    console.warn('Dynamic API URL change not implemented in this version')
  }
}

// 🔄 Funciones de mapeo de datos backend a frontend
function mapPriorityToMapFormat(priority: string): 'BAJA' | 'MEDIA' | 'ALTA' | 'CRITICA' {
  const priorityMap: Record<string, 'BAJA' | 'MEDIA' | 'ALTA' | 'CRITICA'> = {
    'LOW': 'BAJA',
    'MEDIUM': 'MEDIA', 
    'HIGH': 'ALTA',
    'CRITICAL': 'CRITICA',
    'BAJA': 'BAJA',
    'MEDIA': 'MEDIA',
    'ALTA': 'ALTA', 
    'CRITICA': 'CRITICA'
  }
  return priorityMap[priority?.toUpperCase()] || 'MEDIA'
}

function mapStatusToMapFormat(status: string): 'ABIERTA' | 'EN_PROGRESO' | 'RESUELTA' | 'CERRADA' {
  const statusMap: Record<string, 'ABIERTA' | 'EN_PROGRESO' | 'RESUELTA' | 'CERRADA'> = {
    'PENDING': 'ABIERTA',
    'IN_PROGRESS': 'EN_PROGRESO',
    // P13-V7B · the two canonical statuses this table did not know fell through to the default and
    // were drawn as OPEN: a CANCELLED incidence appeared on the map as an open one. An incidence
    // waiting for validation is still in flight; a cancelled one is terminal.
    'PENDING_VALIDATION': 'EN_PROGRESO',
    'CANCELLED': 'CERRADA',
    'RESOLVED': 'RESUELTA',
    'CLOSED': 'CERRADA',
    'ABIERTO': 'ABIERTA',
    'ABIERTA': 'ABIERTA',
    'EN_PROGRESO': 'EN_PROGRESO',
    'RESUELTA': 'RESUELTA',
    'CERRADA': 'CERRADA'
  }
  return statusMap[status?.toUpperCase()] || 'ABIERTA'
}

export default mapService

// Utilidades locales
function cryptoRandomId() {
  return Math.random().toString(36).slice(2)
}

// P13-V5A-R5 · lectura honesta de una coordenada persistida. Devuelve undefined cuando el productor
// no trae valor (null, undefined, cadena vacía o algo no numérico) y conserva el 0, que es una
// coordenada válida. El código anterior usaba `||`, que trataba el 0 como ausencia y además abría la
// puerta al fallback fabricado. No convierte, no estima, no rellena: sólo distingue medido de ausente.
function toFiniteCoordinate(raw: unknown): number | undefined {
  if (raw === null || raw === undefined || raw === '') return undefined
  const value = Number(raw)
  return Number.isFinite(value) ? value : undefined
}

function mapRouteStatus(status: string): Route['status'] {
  const s = (status || '').toUpperCase()
  if (s === 'ACTIVE' || s === 'ACTIVA') return 'ACTIVA'
  if (s === 'PAUSED' || s === 'PAUSADA') return 'PAUSADA'
  if (s === 'COMPLETED' || s === 'COMPLETADA') return 'COMPLETADA'
  return 'ACTIVA'
}
